import {
  MINIMAP_CONFIG,
  type MinimapPalette,
} from "../config/minimap.ts";
import type { LocalHorizontal } from "../world/coordinates.ts";
import type { PolygonWithHoles } from "../world/geometry.ts";
import {
  minimapCardinalDirection,
  minimapHeadingVector,
  projectMinimapPoint,
  type MinimapAreaFeature,
  type MinimapLineFeature,
  type MinimapTileFeatures,
} from "../world/minimap.ts";

export type MinimapTone = "light" | "dark";
export type MinimapSuppressionReason =
  | "visual-study"
  | "interior"
  | "elevator";

export interface MinimapViewModel {
  readonly preferenceVisible: boolean;
  readonly suppressionReason: MinimapSuppressionReason | null;
  readonly center: Readonly<LocalHorizontal>;
  readonly facingYaw: number;
  readonly lookYaw: number;
  readonly waypoint: Readonly<LocalHorizontal> | null;
  readonly streetLabel: string | null;
  readonly tone: MinimapTone;
  readonly tileRevision: number;
  readonly tiles: readonly MinimapTileFeatures[];
}

export interface MinimapDiagnostics {
  readonly preferenceVisible: boolean;
  readonly visible: boolean;
  readonly ready: boolean;
  readonly suppressionReason: MinimapSuppressionReason | null;
  readonly radiusM: number;
  readonly tileCount: number;
  readonly roadCount: number;
  readonly railwayCount: number;
  readonly pathCount: number;
  readonly waterCount: number;
  readonly buildingCount: number;
  readonly vegetationCount: number;
  readonly waypointVisible: boolean;
  readonly waypointDistanceM: number | null;
  readonly drawCount: number;
  readonly lastDrawMs: number;
  readonly maxDrawMs: number;
  readonly lastBaseBuildMs: number;
  readonly maxBaseBuildMs: number;
}

export interface Minimap {
  update(viewModel: Readonly<MinimapViewModel>): void;
  getDiagnostics(): MinimapDiagnostics;
  dispose(): void;
}

type MinimapConfig = typeof MINIMAP_CONFIG;

function createElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tagName);
  element.className = className;
  return element;
}

function appendRing(
  path: Path2D,
  ring: readonly Readonly<LocalHorizontal>[],
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  sizePx: number,
): void {
  const first = ring[0];
  if (first === undefined) {
    return;
  }
  const projectedFirst = projectMinimapPoint(first, center, radiusM, sizePx);
  path.moveTo(projectedFirst.x, projectedFirst.y);
  for (let index = 1; index < ring.length; index += 1) {
    const point = ring[index];
    if (point === undefined) {
      continue;
    }
    const projected = projectMinimapPoint(point, center, radiusM, sizePx);
    path.lineTo(projected.x, projected.y);
  }
  path.closePath();
}

function polygonPath(
  polygon: Readonly<PolygonWithHoles>,
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  sizePx: number,
): Path2D {
  const path = new Path2D();
  appendRing(path, polygon.outer, center, radiusM, sizePx);
  for (const hole of polygon.holes) {
    appendRing(path, hole, center, radiusM, sizePx);
  }
  return path;
}

function drawAreas(
  context: CanvasRenderingContext2D,
  features: readonly MinimapAreaFeature[],
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  sizePx: number,
  fillStyle: string,
  strokeStyle: string | null,
  strokeWidthPx: number,
): void {
  context.fillStyle = fillStyle;
  context.strokeStyle = strokeStyle ?? fillStyle;
  context.lineWidth = strokeWidthPx;
  for (const feature of features) {
    for (const polygon of feature.polygons) {
      const path = polygonPath(polygon, center, radiusM, sizePx);
      context.fill(path, "evenodd");
      if (strokeStyle !== null && strokeWidthPx > 0) {
        context.stroke(path);
      }
    }
  }
}

function linePath(
  feature: Readonly<MinimapLineFeature>,
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  sizePx: number,
): Path2D | null {
  const first = feature.points[0];
  if (first === undefined) {
    return null;
  }
  const path = new Path2D();
  const projectedFirst = projectMinimapPoint(first, center, radiusM, sizePx);
  path.moveTo(projectedFirst.x, projectedFirst.y);
  for (let index = 1; index < feature.points.length; index += 1) {
    const point = feature.points[index];
    if (point === undefined) {
      continue;
    }
    const projected = projectMinimapPoint(point, center, radiusM, sizePx);
    path.lineTo(projected.x, projected.y);
  }
  return path;
}

function drawLines(
  context: CanvasRenderingContext2D,
  features: readonly MinimapLineFeature[],
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  sizePx: number,
  strokeStyle: string,
  lineWidth: (feature: Readonly<MinimapLineFeature>) => number,
  dash: readonly number[] = [],
): void {
  context.strokeStyle = strokeStyle;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.setLineDash([...dash]);
  for (const feature of features) {
    const path = linePath(feature, center, radiusM, sizePx);
    if (path === null) {
      continue;
    }
    context.lineWidth = lineWidth(feature);
    context.stroke(path);
  }
  context.setLineDash([]);
}

function now(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

export function createMinimap(
  root: HTMLElement,
  config: MinimapConfig = MINIMAP_CONFIG,
): Minimap {
  const compactMedia = window.matchMedia(
    "(max-width: 47.99rem), (hover: none) and (pointer: coarse)",
  );
  const figure = createElement("figure", "hud-minimap");
  figure.setAttribute("aria-live", "off");

  const frame = createElement("div", "hud-minimap-frame");
  const canvas = createElement("canvas", "hud-minimap-canvas");
  canvas.setAttribute("aria-hidden", "true");
  const loading = createElement("span", "hud-minimap-loading");
  loading.textContent = "Charting nearby streets…";
  const north = createElement("span", "hud-minimap-north");
  north.textContent = "N";
  north.setAttribute("aria-hidden", "true");
  const scale = createElement("span", "hud-minimap-scale");
  scale.textContent = "50 m";
  scale.setAttribute("aria-hidden", "true");
  frame.append(canvas, loading, north, scale);

  const caption = createElement("figcaption", "hud-minimap-caption");
  const captionTitle = createElement("span", "hud-minimap-caption-title");
  captionTitle.textContent = "Local survey";
  const captionRange = createElement("span", "hud-minimap-caption-range");
  captionRange.textContent = `${config.radiusM} m radius`;
  caption.append(captionTitle, captionRange);
  figure.append(frame, caption);
  root.append(figure);

  const buffer = document.createElement("canvas");
  let bufferContext: CanvasRenderingContext2D | null = null;
  let bufferAnchor: LocalHorizontal | null = null;
  let bufferRadiusM = config.radiusM + config.overscanM;
  let canvasSizePx = 0;
  let pixelRatio = 1;
  let baseTone: MinimapTone | null = null;
  let baseTileRevision = -1;
  let previousAriaStreet: string | null = null;
  let previousAriaDirection: string | null = null;
  let previousAriaWaypoint: string | null = null;
  let diagnostics: MinimapDiagnostics = {
    preferenceVisible: true,
    visible: true,
    ready: false,
    suppressionReason: null,
    radiusM: config.radiusM,
    tileCount: 0,
    roadCount: 0,
    railwayCount: 0,
    pathCount: 0,
    waterCount: 0,
    buildingCount: 0,
    vegetationCount: 0,
    waypointVisible: false,
    waypointDistanceM: null,
    drawCount: 0,
    lastDrawMs: 0,
    maxDrawMs: 0,
    lastBaseBuildMs: 0,
    maxBaseBuildMs: 0,
  };

  function ensureCanvasSize(): CanvasRenderingContext2D | null {
    const cssSize = Math.max(1, Math.round(canvas.clientWidth || config.sizeCssPx));
    const nextPixelRatio = Math.min(
      config.maxDevicePixelRatio,
      Math.max(1, window.devicePixelRatio || 1),
    );
    const nextSize = Math.max(1, Math.round(cssSize * nextPixelRatio));
    if (canvas.width !== nextSize || canvas.height !== nextSize) {
      canvas.width = nextSize;
      canvas.height = nextSize;
      canvasSizePx = nextSize;
      pixelRatio = nextPixelRatio;
      baseTileRevision = -1;
    }
    return canvas.getContext("2d");
  }

  function roadWidthPx(feature: Readonly<MinimapLineFeature>): number {
    const pixelsPerMetre = canvasSizePx / (config.radiusM * 2);
    const sourceWidthPx = (feature.widthM ?? 5) * pixelsPerMetre;
    return Math.max(
      config.minimumRoadStrokeCssPx * pixelRatio,
      Math.min(config.maximumRoadStrokeCssPx * pixelRatio, sourceWidthPx),
    );
  }

  function rebuildBase(viewModel: Readonly<MinimapViewModel>): number {
    const startedAt = now();
    bufferRadiusM = config.radiusM + config.overscanM;
    const bufferSizePx = Math.ceil(
      canvasSizePx * bufferRadiusM / config.radiusM,
    );
    if (buffer.width !== bufferSizePx || buffer.height !== bufferSizePx) {
      buffer.width = bufferSizePx;
      buffer.height = bufferSizePx;
      bufferContext = buffer.getContext("2d");
    }
    const context = bufferContext;
    if (context === null) {
      return 0;
    }
    bufferAnchor = { ...viewModel.center };
    baseTone = viewModel.tone;
    baseTileRevision = viewModel.tileRevision;
    const palette: MinimapPalette = config.palettes[viewModel.tone];
    const lines = viewModel.tiles.flatMap((tile) => tile.lines);
    const areas = viewModel.tiles.flatMap((tile) => tile.areas);
    const roads = lines.filter((feature) => feature.kind === "road");
    const paths = lines.filter((feature) => feature.kind === "path");
    const railways = lines.filter((feature) => feature.kind === "railway");
    const waters = lines.filter((feature) => feature.kind === "water");
    const vegetation = areas.filter((feature) => feature.kind === "vegetation");
    const buildings = areas.filter((feature) => feature.kind === "building");

    context.clearRect(0, 0, buffer.width, buffer.height);
    context.fillStyle = palette.ground;
    context.fillRect(0, 0, buffer.width, buffer.height);
    drawAreas(
      context,
      vegetation,
      bufferAnchor,
      bufferRadiusM,
      bufferSizePx,
      palette.vegetation,
      null,
      0,
    );
    drawLines(
      context,
      waters,
      bufferAnchor,
      bufferRadiusM,
      bufferSizePx,
      palette.water,
      () => config.waterStrokeCssPx * pixelRatio,
    );
    drawLines(
      context,
      railways,
      bufferAnchor,
      bufferRadiusM,
      bufferSizePx,
      palette.railwayCasing,
      () =>
        (config.railwayStrokeCssPx + config.railwayCasingExtraCssPx) *
        pixelRatio,
    );
    drawLines(
      context,
      railways,
      bufferAnchor,
      bufferRadiusM,
      bufferSizePx,
      palette.railway,
      () => config.railwayStrokeCssPx * pixelRatio,
      [4 * pixelRatio, 2.5 * pixelRatio],
    );
    drawLines(
      context,
      roads,
      bufferAnchor,
      bufferRadiusM,
      bufferSizePx,
      palette.roadCasing,
      (feature) => roadWidthPx(feature) + config.roadCasingExtraCssPx * pixelRatio,
    );
    drawLines(
      context,
      roads,
      bufferAnchor,
      bufferRadiusM,
      bufferSizePx,
      palette.road,
      roadWidthPx,
    );
    drawLines(
      context,
      paths,
      bufferAnchor,
      bufferRadiusM,
      bufferSizePx,
      palette.path,
      () => config.pathStrokeCssPx * pixelRatio,
      [3 * pixelRatio, 2.5 * pixelRatio],
    );
    drawAreas(
      context,
      buildings,
      bufferAnchor,
      bufferRadiusM,
      bufferSizePx,
      palette.building,
      palette.buildingEdge,
      config.buildingStrokeCssPx * pixelRatio,
    );

    diagnostics = {
      ...diagnostics,
      ready: viewModel.tiles.length > 0,
      tileCount: viewModel.tiles.length,
      roadCount: roads.length,
      railwayCount: railways.length,
      pathCount: paths.length,
      waterCount: waters.length,
      buildingCount: buildings.length,
      vegetationCount: vegetation.length,
    };
    return now() - startedAt;
  }

  function drawPlayerOverlay(
    context: CanvasRenderingContext2D,
    viewModel: Readonly<MinimapViewModel>,
  ): void {
    const palette: MinimapPalette = config.palettes[viewModel.tone];
    const center = canvasSizePx / 2;
    const viewVector = minimapHeadingVector(viewModel.lookYaw);
    const coneLength = config.viewDirectionLengthCssPx * pixelRatio;
    const coneHalfWidth = Math.tan(config.viewDirectionHalfAngleRadians) * coneLength;
    context.save();
    context.translate(center, center);
    context.rotate(Math.atan2(viewVector.y, viewVector.x));
    context.beginPath();
    context.moveTo(0, 0);
    context.lineTo(coneLength, -coneHalfWidth);
    context.lineTo(coneLength, coneHalfWidth);
    context.closePath();
    context.fillStyle = palette.viewCone;
    context.fill();
    context.restore();

    const facingVector = minimapHeadingVector(viewModel.facingYaw);
    const markerLength = config.playerMarkerLengthCssPx * pixelRatio;
    const markerHalfWidth = config.playerMarkerWidthCssPx * pixelRatio / 2;
    context.save();
    context.translate(center, center);
    context.rotate(Math.atan2(facingVector.y, facingVector.x));
    context.beginPath();
    context.moveTo(markerLength / 2, 0);
    context.lineTo(-markerLength / 2, markerHalfWidth);
    context.lineTo(-markerLength / 2, -markerHalfWidth);
    context.closePath();
    context.fillStyle = palette.player;
    context.strokeStyle = palette.playerOutline;
    context.lineWidth = Math.max(1, pixelRatio);
    context.fill();
    context.stroke();
    context.restore();
  }

  function drawWaypointOverlay(
    context: CanvasRenderingContext2D,
    viewModel: Readonly<MinimapViewModel>,
  ): void {
    if (viewModel.waypoint === null) return;
    const palette: MinimapPalette = config.palettes[viewModel.tone];
    const deltaX = viewModel.waypoint.x - viewModel.center.x;
    const deltaZ = viewModel.waypoint.z - viewModel.center.z;
    const distanceM = Math.hypot(deltaX, deltaZ);
    const maximumPixelRadius = canvasSizePx / 2 -
      config.waypointEdgeInsetCssPx * pixelRatio;
    const projectedDistancePx = distanceM / config.radiusM * canvasSizePx / 2;
    const displayDistancePx = Math.min(maximumPixelRadius, projectedDistancePx);
    const directionX = distanceM <= Number.EPSILON ? 0 : deltaX / distanceM;
    const directionY = distanceM <= Number.EPSILON ? 0 : deltaZ / distanceM;
    const markerX = canvasSizePx / 2 + directionX * displayDistancePx;
    const markerY = canvasSizePx / 2 + directionY * displayDistancePx;
    const radiusPx = config.waypointMarkerRadiusCssPx * pixelRatio;

    context.save();
    context.translate(markerX, markerY);
    context.rotate(Math.atan2(directionY, directionX) + Math.PI / 2);
    context.beginPath();
    context.arc(0, -radiusPx * 0.32, radiusPx, 0, Math.PI * 2);
    context.moveTo(-radiusPx * 0.62, radiusPx * 0.24);
    context.lineTo(0, radiusPx * 1.38);
    context.lineTo(radiusPx * 0.62, radiusPx * 0.24);
    context.closePath();
    context.fillStyle = palette.waypoint;
    context.strokeStyle = palette.waypointOutline;
    context.lineWidth = Math.max(1, pixelRatio);
    context.fill();
    context.stroke();
    context.restore();
  }

  function updateAria(viewModel: Readonly<MinimapViewModel>): void {
    const direction = minimapCardinalDirection(viewModel.facingYaw);
    const waypoint = viewModel.waypoint === null
      ? null
      : `${Math.round(Math.hypot(
          viewModel.waypoint.x - viewModel.center.x,
          viewModel.waypoint.z - viewModel.center.z,
        ) / 10) * 10}`;
    if (
      direction === previousAriaDirection &&
      viewModel.streetLabel === previousAriaStreet &&
      waypoint === previousAriaWaypoint
    ) {
      return;
    }
    previousAriaDirection = direction;
    previousAriaStreet = viewModel.streetLabel;
    previousAriaWaypoint = waypoint;
    const place = viewModel.streetLabel === null
      ? "nearby streets"
      : viewModel.streetLabel;
    figure.setAttribute(
      "aria-label",
      `Local map, north up, facing ${direction} near ${place}${
        waypoint === null ? "" : `, waypoint ${waypoint} metres away`
      }`,
    );
  }

  function update(viewModel: Readonly<MinimapViewModel>): void {
    const visible =
      viewModel.preferenceVisible &&
      viewModel.suppressionReason === null &&
      !compactMedia.matches;
    figure.hidden = !visible;
    figure.dataset.tone = viewModel.tone;
    figure.dataset.suppressedReason = viewModel.suppressionReason ?? "none";
    diagnostics = {
      ...diagnostics,
      preferenceVisible: viewModel.preferenceVisible,
      visible,
      suppressionReason: viewModel.suppressionReason,
      waypointVisible: visible && viewModel.waypoint !== null,
      waypointDistanceM: viewModel.waypoint === null
        ? null
        : Math.hypot(
            viewModel.waypoint.x - viewModel.center.x,
            viewModel.waypoint.z - viewModel.center.z,
          ),
    };
    figure.dataset.waypoint = String(viewModel.waypoint !== null);
    updateAria(viewModel);
    if (!visible) {
      return;
    }
    const context = ensureCanvasSize();
    if (context === null) {
      diagnostics = { ...diagnostics, ready: false };
      loading.hidden = false;
      figure.dataset.ready = "false";
      return;
    }

    const startedAt = now();
    const movedFromAnchor = bufferAnchor === null
      ? Number.POSITIVE_INFINITY
      : Math.hypot(
          viewModel.center.x - bufferAnchor.x,
          viewModel.center.z - bufferAnchor.z,
        );
    if (
      bufferAnchor === null ||
      baseTone !== viewModel.tone ||
      baseTileRevision !== viewModel.tileRevision ||
      movedFromAnchor >= config.rebuildDistanceM
    ) {
      const baseBuildMs = rebuildBase(viewModel);
      diagnostics = {
        ...diagnostics,
        lastBaseBuildMs: baseBuildMs,
        maxBaseBuildMs: Math.max(diagnostics.maxBaseBuildMs, baseBuildMs),
      };
    }

    const anchor = bufferAnchor;
    if (anchor === null || bufferContext === null) {
      return;
    }
    const bufferPixelsPerMetre = buffer.width / (bufferRadiusM * 2);
    const sourceCenterX = buffer.width / 2 +
      (viewModel.center.x - anchor.x) * bufferPixelsPerMetre;
    const sourceCenterY = buffer.height / 2 +
      (viewModel.center.z - anchor.z) * bufferPixelsPerMetre;
    context.clearRect(0, 0, canvasSizePx, canvasSizePx);
    context.drawImage(
      buffer,
      sourceCenterX - canvasSizePx / 2,
      sourceCenterY - canvasSizePx / 2,
      canvasSizePx,
      canvasSizePx,
      0,
      0,
      canvasSizePx,
      canvasSizePx,
    );
    drawPlayerOverlay(context, viewModel);
    drawWaypointOverlay(context, viewModel);

    captionRange.textContent = diagnostics.waypointDistanceM === null
      ? `${config.radiusM} m radius`
      : diagnostics.waypointDistanceM < 1_000
        ? `${Math.round(diagnostics.waypointDistanceM)} m to marker`
        : `${(diagnostics.waypointDistanceM / 1_000).toFixed(1)} km to marker`;

    const drawMs = now() - startedAt;
    diagnostics = {
      ...diagnostics,
      drawCount: diagnostics.drawCount + 1,
      lastDrawMs: drawMs,
      maxDrawMs: Math.max(diagnostics.maxDrawMs, drawMs),
    };
    loading.hidden = diagnostics.ready;
    figure.dataset.ready = String(diagnostics.ready);
  }

  return {
    update,
    getDiagnostics: () => ({ ...diagnostics }),
    dispose: () => {
      figure.remove();
      buffer.width = 0;
      buffer.height = 0;
    },
  };
}
